import { useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { TbBorderCorners } from 'react-icons/tb';
import { motion, AnimatePresence } from 'framer-motion';

interface LinkedSpacingControlProps {
  title: string;
  valueRoot: number;
  valueTop: number;
  valueRight: number;
  valueBottom: number;
  valueLeft: number;
  onRootChange: (v: number) => void;
  onTopChange: (v: number) => void;
  onRightChange: (v: number) => void;
  onBottomChange: (v: number) => void;
  onLeftChange: (v: number) => void;
}

function LinkedSpacingControl({
  title,
  valueRoot,
  valueTop,
  valueRight,
  valueBottom,
  valueLeft,
  onRootChange,
  onTopChange,
  onRightChange,
  onBottomChange,
  onLeftChange,
}: LinkedSpacingControlProps) {
  const [showCorners, setShowCorners] = useState(false);

  return (
    <div className="rounded-md border border-gray-200 p-2">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-700">{title}</div>
        <button
          type="button"
          onClick={() => setShowCorners((s) => !s)}
          className="rounded border border-blue-200 bg-blue-50 px-2 py-[2px] text-[10px] font-medium text-blue-700 hover:bg-blue-100"
          title={
            showCorners ? 'Hide individual sides' : 'Edit individual sides'
          }
        >
          {showCorners ? (
            <IoIosClose size={14} />
          ) : (
            <TbBorderCorners size={14} />
          )}
        </button>
      </div>

      {/* Root */}
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs text-gray-600">Root</label>
        <input
          type="number"
          className="ml-2 w-20 rounded border border-gray-300 p-1 text-[12px]"
          value={Number(valueRoot)}
          onChange={(e) => onRootChange(Number(e.target.value || 0))}
          title="Set the same value for all sides"
        />
      </div>

      <AnimatePresence>
        {showCorners && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-600">Top</label>
                <input
                  type="number"
                  className="w-full rounded border border-gray-300 p-1 text-[12px]"
                  value={Number(valueTop)}
                  onChange={(e) => onTopChange(Number(e.target.value || 0))}
                  title="Top spacing"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-600">Right</label>
                <input
                  type="number"
                  className="w-full rounded border border-gray-300 p-1 text-[12px]"
                  value={Number(valueRight)}
                  onChange={(e) => onRightChange(Number(e.target.value || 0))}
                  title="Right spacing"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-600">Bottom</label>
                <input
                  type="number"
                  className="w-full rounded border border-gray-300 p-1 text-[12px]"
                  value={Number(valueBottom)}
                  onChange={(e) => onBottomChange(Number(e.target.value || 0))}
                  title="Bottom spacing"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-600">Left</label>
                <input
                  type="number"
                  className="w-full rounded border border-gray-300 p-1 text-[12px]"
                  value={Number(valueLeft)}
                  onChange={(e) => onLeftChange(Number(e.target.value || 0))}
                  title="Left spacing"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LinkedSpacingControl;
